import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Input, Row } from 'antd';
import { TbCircleXFilled, TbLink, TbSearch } from 'react-icons/tb';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { teamService } from '@/services';
import { LearnMoreFooter } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { ResourceCollaborator, ResourceType } from '@/stores/database/entities/00-RBAC/99-Collaborators/types';

import type { InviteableResourceVisibility, InviteableWorkspaceResource } from '../../types';
import { VisibilityDropdown } from './components';
import ResultsList from './components/ResultsList';

interface InviteProps {
    onVisibilityChange?: (visibility: InviteableResourceVisibility) => Promise<void> | void;
    resource: InviteableWorkspaceResource;
    resourceType: ResourceType;
    collaborators: ResourceCollaborator[];
    canInvite: boolean;
    canManage: boolean;
}

const Invite: FC<InviteProps> = ({
    resource,
    resourceType,
    collaborators,
    canInvite,
    canManage,
    onVisibilityChange,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [input, setInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const handleSearch = async (value: string) => {
        setLoading(true);

        try {
            const [memberResults, teamResults] = await Promise.all([
                authorizedAxiosInstance
                    //! This should remain static as it's the one working in prod
                    .post(`/api/search/database/members/investment/invite`, {
                        query: value,
                    }),
                teamService.searchTeams(value),
            ]);

            if (memberResults.status === 200) {
                setSearchResults({ ...memberResults?.data, teams: teamResults });
            }
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const [, cancel] = useDebounce(
        () => {
            if (input !== '') {
                handleSearch(input);
            } else if (input === '') {
                setLoading(false);
            }
        },
        750,
        [input],
    );

    const { token } = useAntDesignTheme();

    return (
        <Form layout='vertical'>
            <Form.Item noStyle>
                <Input
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                    }}
                    prefix={
                        <TbSearch size='1rem' style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />
                    }
                    placeholder='Invite Workspace Members...'
                    allowClear={{
                        clearIcon: <TbCircleXFilled size='1rem' style={{ verticalAlign: '-0.2rem' }} />,
                    }}
                />
            </Form.Item>
            <ResultsList
                loading={loading}
                resource={resource}
                resourceType={resourceType}
                input={input}
                collaborators={collaborators}
                results={searchResults}
                setInput={setInput}
                canInvite={canInvite}
                canManage={canManage}
            />
            <Divider style={{ marginBottom: '1rem', marginTop: 0 }} />
            <Row align={'middle'}>
                {canManage && (
                    <Col flex='none'>
                        <VisibilityDropdown
                            resource={resource}
                            resourceType={resourceType}
                            onVisibilityChange={onVisibilityChange}
                        />
                    </Col>
                )}
                <Col flex='auto' />
                <Col flex='none' style={{ textAlign: 'right' }}>
                    <Button
                        type='dashed'
                        icon={<TbLink size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                        onClick={() => {
                            navigator.clipboard.writeText(window.location.href);

                            // toast('Link Copied', {
                            //     icon: <TbLink size='1rem' />,
                            //     description: resource?.title || resource?.editedName || resource?.name,
                            // });
                        }}
                    >
                        Copy Link
                    </Button>
                </Col>
            </Row>
            <Divider style={{ margin: '1rem 0' }} />
            <LearnMoreFooter align='left' topic='inviting & collaborating' />
        </Form>
    );
};

export default Invite;
